
import React, { useState, useMemo } from 'react';
import { Search, Plus, Building2, UserCircle, PhoneCall, MapPin, ExternalLink, MessageCircle, Pencil, X, Save, Mail, UserPlus, Globe, Hash, LayoutGrid, List, FilePlus, DollarSign, Briefcase, Globe2, FileText, AlignLeft, CreditCard, Tag } from 'lucide-react';
import { OrderStatus, Client } from './types';
import { STATUS_COLORS } from './constants';
import { Card, Badge, TabButton, InfoItem } from './SharedComponents';

export const ClientsPage = ({ clients, orders, onNewClient, onSelectClient, onNewOrder }: any) => {
  const [localSearch, setLocalSearch] = useState('');
  const [viewMode, setViewMode] = useState<'GRID' | 'LIST'>('GRID');

  const filteredClients = useMemo(() => {
    return clients.filter((c: Client) => 
      c.empresa.toLowerCase().includes(localSearch.toLowerCase()) || 
      c.nomeLocal.toLowerCase().includes(localSearch.toLowerCase()) ||
      c.contatoResponsavel.toLowerCase().includes(localSearch.toLowerCase()) ||
      (c.cnpj_cpf && c.cnpj_cpf.includes(localSearch))
    );
  }, [clients, localSearch]);

  const handleContact = (client: Client, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    const phone = client.telefonePrincipal.replace(/\D/g, '');
    const message = `Olá ${client.contatoResponsavel}, sou da Logística. Gostaria de tratar sobre o evento no local: ${client.nomeLocal}.`;
    window.open(`https://wa.me/55${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-700 text-gray-900 p-4 md:p-8 lg:p-10">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
        <div className="space-y-1">
          <h1 className="text-3xl md:text-4xl font-black text-secondary uppercase tracking-tighter italic leading-none">Parceiros & Locais</h1>
          <p className="text-gray-400 text-xs md:text-sm font-medium italic">Controle granular da base de clientes e locais de montagem.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          <button 
            onClick={onNewOrder}
            className="flex-1 lg:flex-none bg-white border-2 border-gray-100 text-secondary px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest flex items-center justify-center gap-2 hover:bg-gray-50 transition-all shadow-sm active:scale-95"
          >
            <Plus size={18} className="text-primary" strokeWidth={3} /> Novo Orçamento
          </button>
          <button 
            onClick={onNewClient} 
            className="flex-1 lg:flex-none bg-primary text-white px-8 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest flex items-center justify-center gap-2 hover:brightness-110 transition-all shadow-xl shadow-primary/20 active:scale-95"
          >
            <UserPlus size={18} strokeWidth={2.5} /> Novo Cliente
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1 group">
          <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary transition-colors" size={20} />
          <input 
            type="text" 
            placeholder="Pesquisar por empresa, CNPJ, local ou responsável..." 
            className="w-full pl-16 pr-6 py-5 bg-white border border-gray-100 rounded-[28px] shadow-sm focus:ring-4 focus:ring-primary/5 outline-none transition-all text-secondary font-bold text-sm"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
          />
        </div>
        <div className="bg-white p-2 rounded-[24px] border border-gray-100 flex items-center gap-1 shadow-sm shrink-0 self-end md:self-auto">
           <button 
            onClick={() => setViewMode('GRID')}
            className={`p-3 rounded-2xl transition-all ${viewMode === 'GRID' ? 'bg-primary text-white shadow-lg' : 'text-gray-300 hover:bg-gray-50'}`}
           >
             <LayoutGrid size={20} />
           </button>
           <button 
            onClick={() => setViewMode('LIST')}
            className={`p-3 rounded-2xl transition-all ${viewMode === 'LIST' ? 'bg-primary text-white shadow-lg' : 'text-gray-300 hover:bg-gray-50'}`}
           >
             <List size={20} />
           </button>
        </div>
      </div>

      {viewMode === 'GRID' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6 lg:gap-8">
          {filteredClients.length > 0 ? filteredClients.map((client: Client) => {
            const clientOrders = orders.filter((o: any) => o.clientId === client.id);
            const activeOrders = clientOrders.filter((o: any) => o.status !== OrderStatus.CONCLUIDO && o.status !== OrderStatus.CANCELADO).length;

            return (
              <Card key={client.id} className="group hover:border-primary/30 transition-all border-2 flex flex-col h-full bg-white rounded-[40px]" onClick={() => onSelectClient(client)}>
                <div className="p-6 lg:p-8 flex-1 flex flex-col">
                  <div className="flex justify-between items-start mb-6">
                    <div className="w-14 h-14 bg-gray-50 text-primary rounded-2xl flex items-center justify-center font-black text-2xl group-hover:bg-primary group-hover:text-white transition-all shadow-inner overflow-hidden">
                      {client.logo ? <img src={client.logo} className="w-full h-full object-contain" /> : client.empresa.charAt(0)}
                    </div>
                    <Badge className={activeOrders > 0 ? "bg-green-50 text-green-700 border-green-100" : "bg-gray-50 text-gray-400 border-gray-100"}>
                      {activeOrders} ATIVAS
                    </Badge>
                  </div>

                  <h3 className="text-xl font-black text-secondary mb-1 uppercase tracking-tight italic leading-tight">{client.empresa}</h3>
                  <p className="text-[10px] text-gray-400 flex items-center gap-1.5 mb-6 font-bold uppercase tracking-widest italic">
                    <MapPin size={12} className="text-primary" /> {client.nomeLocal}
                  </p>

                  <div className="space-y-4 pt-6 border-t border-gray-50 flex-1">
                    <InfoItem label="Responsável" value={client.contatoResponsavel} icon={UserCircle} />
                    <InfoItem label="CNPJ / CPF" value={client.cnpj_cpf} icon={Hash} />
                    <InfoItem label="Cidade/UF" value={`${client.cidade} - ${client.uf}`} icon={Globe} />
                  </div>

                  <div className="mt-8 flex gap-3">
                    <button onClick={(e) => { e.stopPropagation(); onSelectClient(client); }} className="flex-1 py-4 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-2xl text-[9px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 border border-gray-100 active:scale-95">
                      <ExternalLink size={16} /> Detalhes
                    </button>
                    <button onClick={(e) => handleContact(client, e)} className="flex-1 py-4 bg-primary/5 hover:bg-primary/10 text-primary rounded-2xl text-[9px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 border border-primary/10 active:scale-95">
                      <MessageCircle size={16} /> Contato
                    </button>
                  </div>
                </div>
              </Card>
            );
          }) : (
            <div className="col-span-full py-32 text-center flex flex-col items-center justify-center gap-4 opacity-30">
               <Building2 size={64} strokeWidth={1} />
               <p className="text-xs font-black uppercase tracking-[0.4em]">Nenhum parceiro localizado</p>
            </div>
          )}
        </div>
      ) : (
        <Card className="overflow-hidden border-none shadow-xl bg-white rounded-[40px]">
          <div className="overflow-x-auto scrollbar-hide">
            <table className="w-full text-left border-collapse min-w-[1000px]">
              <thead>
                <tr className="bg-gray-50/50 text-gray-400 text-[9px] uppercase font-black tracking-[0.2em] border-b border-gray-100">
                  <th className="px-8 py-6">Parceiro / Empresa</th>
                  <th className="px-8 py-6">CNPJ / CPF</th>
                  <th className="px-8 py-6">Responsável</th>
                  <th className="px-8 py-6">Localização Principal</th>
                  <th className="px-8 py-6 text-center">Status</th>
                  <th className="px-8 py-6 text-right">Ação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredClients.map((client: Client) => {
                  const clientOrders = orders.filter((o: any) => o.clientId === client.id);
                  const activeOrdersCount = clientOrders.filter((o: any) => o.status !== OrderStatus.CONCLUIDO && OrderStatus.CANCELADO).length;
                  
                  return (
                    <tr key={client.id} className="hover:bg-primary/[0.02] transition-colors group cursor-pointer" onClick={() => onSelectClient(client)}>
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-gray-100 text-primary rounded-xl flex items-center justify-center font-black text-sm group-hover:bg-primary group-hover:text-white transition-all shadow-sm">
                            {client.logo ? <img src={client.logo} className="w-full h-full object-contain" /> : client.empresa.charAt(0)}
                          </div>
                          <div>
                            <p className="font-black text-secondary uppercase tracking-tight text-sm">{client.empresa}</p>
                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">ID: {client.id.slice(-4)}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6 font-mono text-xs text-slate-500">{client.cnpj_cpf || '---'}</td>
                      <td className="px-8 py-6">
                        <div className="flex flex-col">
                           <span className="text-xs font-bold text-secondary uppercase">{client.contatoResponsavel}</span>
                           <span className="text-[10px] font-medium text-gray-400">{client.telefonePrincipal}</span>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex flex-col">
                           <span className="text-xs font-bold text-secondary uppercase">{client.nomeLocal}</span>
                           <span className="text-[10px] text-gray-400 max-w-[280px] truncate italic font-medium">{client.enderecoCompleto}</span>
                        </div>
                      </td>
                      <td className="px-8 py-6 text-center">
                        <Badge className={activeOrdersCount > 0 ? "bg-green-50 text-green-700 border-green-100" : "bg-gray-50 text-gray-400 border-gray-100"}>
                          {activeOrdersCount} ATIVAS
                        </Badge>
                      </td>
                      <td className="px-8 py-6 text-right">
                        <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                           <button onClick={(e) => handleContact(client, e)} className="p-3 bg-green-50 text-green-600 rounded-xl hover:bg-green-600 hover:text-white transition-all shadow-sm active:scale-90">
                              <MessageCircle size={18} />
                           </button>
                           <button onClick={(e) => { e.stopPropagation(); onSelectClient(client); }} className="p-3 bg-white border border-gray-100 text-gray-400 rounded-xl hover:text-primary hover:border-primary/20 transition-all shadow-sm active:scale-90">
                              <ExternalLink size={18} />
                           </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export const NewClientModal = ({ onClose, onSubmit, client, addNotification }: any) => {
  const isEdit = !!client;
  const [formData, setFormData] = useState({
    empresa: client?.empresa || '',
    cnpj_cpf: client?.cnpj_cpf || '',
    inscricao_estadual: client?.inscricao_estadual || '',
    contatoResponsavel: client?.contatoResponsavel || '',
    nomeLocal: client?.nomeLocal || '',
    segmento: client?.segmento || '',
    website: client?.website || '',
    cep: client?.cep || '',
    logradouro: client?.logradouro || '',
    numero: client?.numero || '',
    complemento: client?.complemento || '',
    bairro: client?.bairro || '',
    cidade: client?.cidade || '',
    uf: client?.uf || 'SP',
    emailContato: client?.emailContato || '',
    telefonePrincipal: client?.telefonePrincipal || '',
    observacoes: client?.observacoes || '',
    logo: client?.logo || ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.empresa || !formData.contatoResponsavel || !formData.nomeLocal) {
      addNotification("Atenção", "Por favor, preencha os campos obrigatórios.", "warning");
      return;
    }
    
    const enderecoCompleto = `${formData.logradouro}, ${formData.numero}${formData.complemento ? ` (${formData.complemento})` : ''}, ${formData.bairro}, ${formData.cidade} - ${formData.uf}`;

    const finalClient: Client = {
      ...formData,
      id: client?.id || `cli-${Date.now()}`,
      enderecoCompleto
    };
    onSubmit(finalClient);
  };

  return (
    <div className="fixed inset-0 bg-secondary/80 backdrop-blur-sm z-[110] flex items-center justify-center p-4 text-gray-900">
      <div className="bg-white rounded-[40px] w-full max-w-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
        <div className="px-8 md:px-10 py-8 border-b border-gray-100 flex items-center justify-between bg-primary text-white shrink-0">
          <div className="flex items-center gap-4">
            <div className="bg-white/20 p-3 rounded-2xl shadow-inner">
              <UserPlus size={28} />
            </div>
            <div>
              <h2 className="text-2xl font-black uppercase tracking-tight italic leading-none">{isEdit ? 'Editar Registro' : 'Novo Cadastro'}</h2>
              <p className="text-[10px] text-white/60 font-bold uppercase tracking-widest mt-2">Base de Dados Unificada</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors active:scale-90"><X size={32} /></button>
        </div>
        
        <form className="p-8 md:p-10 space-y-10 overflow-y-auto scrollbar-hide flex-1" onSubmit={handleSubmit}>
          
          <section className="space-y-6">
            <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.3em] flex items-center gap-2 border-b border-gray-50 pb-2 italic">
              <Building2 size={16} /> Identificação & Contato
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <InputGroup label="Nome da Empresa / Cliente" value={formData.empresa} onChange={(v: string) => setFormData({...formData, empresa: v})} placeholder="Ex: Casamentos S.A." required />
              <InputGroup label="Responsável" value={formData.contatoResponsavel} onChange={(v: string) => setFormData({...formData, contatoResponsavel: v})} placeholder="Ex: Maria Clara" required />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-1 gap-5">
              <InputGroup label="Apelido do Local (OS)" value={formData.nomeLocal} onChange={(v: string) => setFormData({...formData, nomeLocal: v})} placeholder="Ex: Sede Itu" required />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <InputGroup label="CNPJ / CPF" value={formData.cnpj_cpf} onChange={(v: string) => setFormData({...formData, cnpj_cpf: v})} placeholder="00.000.000/0001-00" />
              <InputGroup label="Inscrição Estadual" value={formData.inscricao_estadual} onChange={(v: string) => setFormData({...formData, inscricao_estadual: v})} placeholder="Isento" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
               <InputGroup label="Ramo / Segmento" value={formData.segmento} onChange={(v: string) => setFormData({...formData, segmento: v})} placeholder="Ex: Produção de Eventos" />
               <InputGroup label="Link da Logo (URL)" value={formData.logo} onChange={(v: string) => setFormData({...formData, logo: v})} placeholder="https://..." />
            </div>
          </section>

          <section className="space-y-6">
            <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.3em] flex items-center gap-2 border-b border-gray-50 pb-2 italic">
              <MapPin size={16} /> Logística de Entrega
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
              <div className="md:col-span-1">
                <InputGroup label="CEP" value={formData.cep} onChange={(v: string) => setFormData({...formData, cep: v})} placeholder="00000-000" />
              </div>
              <div className="md:col-span-3">
                <InputGroup label="Logradouro (Rua/Av)" value={formData.logradouro} onChange={(v: string) => setFormData({...formData, logradouro: v})} placeholder="Ex: Av. Paulista" required />
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              <InputGroup label="Número" value={formData.numero} onChange={(v: string) => setFormData({...formData, numero: v})} placeholder="123" required />
              <InputGroup label="Bairro" value={formData.bairro} onChange={(v: string) => setFormData({...formData, bairro: v})} placeholder="Ex: Centro" required />
              <InputGroup label="Complemento" value={formData.complemento} onChange={(v: string) => setFormData({...formData, complemento: v})} placeholder="Apto, Bloco..." />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
              <div className="md:col-span-3">
                <InputGroup label="Cidade" value={formData.cidade} onChange={(v: string) => setFormData({...formData, cidade: v})} placeholder="Ex: Sorocaba" required />
              </div>
              <div className="md:col-span-1">
                <InputGroup label="UF" value={formData.uf} onChange={(v: string) => setFormData({...formData, uf: v.toUpperCase()})} placeholder="SP" maxLength={2} center />
              </div>
            </div>
          </section>

          <section className="space-y-6">
            <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.3em] flex items-center gap-2 border-b border-gray-50 pb-2 italic">
              <Mail size={16} /> Canais Diretos
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <InputGroup label="E-mail de Contato" type="email" value={formData.emailContato} onChange={(v: string) => setFormData({...formData, emailContato: v})} placeholder="cliente@email.com" required />
              <InputGroup label="Telefone / WhatsApp" value={formData.telefonePrincipal} onChange={(v: string) => setFormData({...formData, telefonePrincipal: v})} placeholder="(00) 00000-0000" required />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-1 gap-5">
              <InputGroup label="Website Corporativo" value={formData.website} onChange={(v: string) => setFormData({...formData, website: v})} placeholder="www.empresa.com" />
            </div>
            <div className="space-y-2">
               <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Observações Estratégicas</label>
               <textarea 
                rows={3}
                className="w-full bg-gray-50 border border-gray-100 p-4 rounded-2xl outline-none font-bold text-secondary text-sm focus:ring-4 focus:ring-primary/5 transition-all shadow-inner"
                value={formData.observacoes}
                onChange={e => setFormData({...formData, observacoes: e.target.value})}
                placeholder="Histórico, preferências or restrições do cliente..."
               />
            </div>
          </section>

          <div className="pt-6 flex flex-col sm:flex-row gap-4 sticky bottom-0 bg-white py-4 border-t border-gray-100">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-5 rounded-2xl border-2 border-gray-100 font-black text-gray-400 uppercase text-[10px] tracking-widest hover:bg-gray-50 transition-all active:scale-95">Cancelar</button>
            <button type="submit" className="flex-[2] px-4 py-5 rounded-2xl bg-primary text-white font-black uppercase text-[10px] tracking-widest hover:brightness-110 transition-all shadow-xl shadow-primary/20 flex items-center justify-center gap-2 active:scale-95">
              <Save size={18} /> {isEdit ? 'Atualizar Registro' : 'Efetivar Registro'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const InputGroup = ({ label, value, onChange, placeholder, type = "text", required, maxLength, center }: any) => (
  <div className="space-y-2">
    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">{label}{required && <span className="text-primary ml-0.5">*</span>}</label>
    <input 
      type={type} 
      required={required}
      maxLength={maxLength}
      className={`w-full bg-gray-50 border border-gray-100 p-4 rounded-2xl focus:ring-4 focus:ring-primary/5 focus:bg-white outline-none text-secondary font-bold shadow-inner transition-all placeholder:text-gray-300 text-sm ${center ? 'text-center' : ''}`} 
      placeholder={placeholder} 
      value={value} 
      onChange={e => onChange(e.target.value)} 
    />
  </div>
);

export const ClientDetailsPanel = ({ client, orders, onClose, onEdit, onSelectOrder }: any) => {
  const [activeTab, setActiveTab] = useState('profile');
  const clientOrders = useMemo(() => orders.filter((o: any) => o.clientId === client.id), [client.id, orders]);
  
  return (
    <div className="fixed inset-0 bg-secondary/70 backdrop-blur-md z-[500] flex justify-end animate-in fade-in duration-300">
      <div className="w-full max-w-2xl bg-[#fcfdfe] h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-500 border-l border-white/20 overflow-hidden">
        <div className="p-8 md:p-10 border-b border-gray-100 flex items-center justify-between bg-white shrink-0">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 bg-primary text-white rounded-[24px] flex items-center justify-center font-black text-3xl shadow-xl italic overflow-hidden">
              {client.logo ? <img src={client.logo} className="w-full h-full object-contain bg-white" /> : client.empresa.charAt(0)}
            </div>
            <div>
              <h2 className="text-3xl font-black text-secondary uppercase tracking-tighter leading-none italic">{client.empresa}</h2>
              <div className="flex items-center gap-3 mt-2">
                 <p className="text-[10px] text-primary font-black flex items-center gap-1.5 uppercase tracking-[0.2em]">
                   <MapPin size={12} strokeWidth={3} /> {client.nomeLocal}
                 </p>
                 {client.segmento && (
                   <Badge className="bg-secondary/5 text-secondary border-none text-[8px]">{client.segmento}</Badge>
                 )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { onEdit(); }} className="p-3 bg-gray-50 hover:bg-white rounded-2xl text-gray-400 hover:text-primary transition-all shadow-sm border border-transparent hover:border-gray-100 active:scale-90" title="Editar Cliente">
              <Pencil size={20} />
            </button>
            <button onClick={onClose} className="p-3 bg-gray-50 hover:bg-white rounded-2xl text-gray-400 hover:text-red-500 transition-all shadow-sm border border-transparent hover:border-gray-100 active:scale-90">
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="flex border-b border-gray-100 px-6 md:px-10 bg-white shrink-0">
          <TabButton active={activeTab === 'profile'} onClick={() => setActiveTab('profile')}>Perfil Completo</TabButton>
          <TabButton active={activeTab === 'history'} onClick={() => setActiveTab('history')}>Histórico de O.S.</TabButton>
        </div>

        <div className="flex-1 overflow-y-auto p-8 md:p-10 space-y-12 scrollbar-hide bg-[#fcfdfe]">
          {activeTab === 'profile' && (
            <div className="space-y-10 animate-in fade-in">
              <section className="space-y-6">
                <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.3em] border-b border-gray-50 pb-3 flex items-center gap-2 italic">
                  <CreditCard size={16} /> Identificação Fiscal & Jurídica
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <InfoItem label="Razão Social / Nome" value={client.empresa} icon={Building2} />
                  <InfoItem label="CNPJ / CPF" value={client.cnpj_cpf} icon={Hash} />
                  <InfoItem label="Inscrição Estadual" value={client.inscricao_estadual || 'Isento'} icon={FileText} />
                  <InfoItem label="Ramo de Atuação" value={client.segmento || 'Não Definido'} icon={Briefcase} />
                </div>
              </section>

              <section className="space-y-6">
                <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.3em] border-b border-gray-50 pb-3 flex items-center gap-2 italic">
                  <MapPin size={16} /> Endereço Técnico de Operação
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <InfoItem label="Apelido do Local" value={client.nomeLocal} icon={Tag} />
                  <InfoItem label="CEP de Destino" value={client.cep} icon={MapPin} />
                  <InfoItem label="Logradouro" value={client.logradouro} />
                  <InfoItem label="Número / Comp." value={`${client.numero} ${client.complemento ? `(${client.complemento})` : ''}`} />
                  <InfoItem label="Bairro" value={client.bairro} />
                  <InfoItem label="Cidade / UF" value={`${client.cidade} - ${client.uf}`} />
                </div>
                <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 italic">
                   <p className="text-[8px] font-black text-gray-300 uppercase mb-1">Endereço Unificado:</p>
                   <p className="text-xs font-bold text-slate-500 leading-relaxed">{client.enderecoCompleto}</p>
                </div>
              </section>

              <section className="space-y-6">
                <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.4em] border-b border-gray-50 pb-3 flex items-center gap-2 italic">
                  <Mail size={16} /> Canais Diretos
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <InfoItem label="Responsável Logístico" value={client.contatoResponsavel} icon={UserCircle} />
                  <InfoItem label="WhatsApp / Telefone" value={client.telefonePrincipal} icon={PhoneCall} className="text-green-600" />
                  <InfoItem label="E-mail de Contato" value={client.emailContato} icon={Mail} />
                  <div className="space-y-1">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">Página Web</p>
                    {client.website ? (
                       <a href={client.website.startsWith('http') ? client.website : `https://${client.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs md:text-sm font-bold text-blue-500 hover:underline">
                          <Globe2 size={14} /> {client.website}
                       </a>
                    ) : <span className="text-sm font-bold text-slate-300 italic">Não informado</span>}
                  </div>
                </div>
              </section>

              <section className="space-y-6">
                 <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.4em] border-b border-gray-50 pb-3 flex items-center gap-2 italic">
                    <AlignLeft size={16} /> Observações Estratégicas
                 </h3>
                 <div className="bg-slate-50/50 p-6 rounded-[32px] border-2 border-slate-100 shadow-inner">
                    <p className="text-sm font-medium text-slate-600 leading-relaxed italic">
                       {client.observacoes || "Nenhuma nota estratégica registrada para este parceiro."}
                    </p>
                 </div>
              </section>

              <section className="space-y-6">
                <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.3em] border-b border-gray-100 pb-3 flex items-center gap-2 italic">
                  <ExternalLink size={16} /> Performance Logística
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-6 bg-white rounded-3xl border border-gray-100 shadow-sm flex flex-col justify-between h-32">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none">Eventos Realizados</p>
                    <p className="text-4xl font-black text-secondary tracking-tighter">{clientOrders.length}</p>
                  </div>
                  <div className="p-6 bg-secondary text-white rounded-3xl shadow-xl flex flex-col justify-between h-32 border-4 border-white/5 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-3 opacity-5 rotate-12"><DollarSign size={64} /></div>
                    <p className="text-[10px] font-black text-primary uppercase tracking-widest leading-none relative z-10">LTV Acumulado</p>
                    <p className="text-2xl font-black text-white relative z-10">R$ {clientOrders.reduce((acc: any, o: any) => acc + (o.estimatedCost || 0), 0).toLocaleString('pt-BR')}</p>
                  </div>
                </div>
              </section>
            </div>
          )}
          
          {activeTab === 'history' && (
            <div className="space-y-4 animate-in fade-in">
              {clientOrders.length > 0 ? clientOrders.map((o: any) => (
                <div key={o.id} onClick={() => onSelectOrder(o)} className="flex items-center justify-between p-6 bg-white border-2 border-gray-50 rounded-[32px] hover:border-primary/20 hover:bg-primary/[0.01] cursor-pointer transition-all group shadow-sm">
                  <div className="min-w-0">
                    <p className="font-black text-secondary uppercase tracking-tight group-hover:text-primary transition-colors truncate">{o.nomeEvento}</p>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1 italic">{o.dataEvento.split('-').reverse().join('/')} • ID: {o.id}</p>
                  </div>
                  <Badge className={`${STATUS_COLORS[o.status as OrderStatus] || 'bg-gray-100'} border-none px-3 py-1 font-black text-[9px] shadow-sm ml-4 shrink-0`}>{o.status}</Badge>
                </div>
              )) : (
                <div className="text-center py-32 flex flex-col items-center justify-center gap-4 opacity-20">
                  <FilePlus size={64} strokeWidth={1} />
                  <p className="font-black uppercase text-xs tracking-[0.4em]">Sem histórico de ordens</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
